import { Component as ComponentClass } from '../../base/Component';
import { isIntersectionObserverSupported } from '../../utils/listeners';
import { clamp } from '../../utils/math';
import { normalizedTimeoutCallback } from '../../utils/common';
/**
 * Detect when elements enter or leave the viewport
 */
export class ScrollView extends ComponentClass {
    _getDefaultProps() {
        return Object.assign(Object.assign({}, super._getDefaultProps()), { intersectionRoot: null, isEnabled: true, rootMargin: 0.05, states: 'in', classToToggle: 'viewed', hasDelay: true, maxDelay: 1000, direction: 'vertical', viewportTarget: 'any', resizeDebounce: 0 });
    }
    /** If first start */
    get isFirstStart() {
        return this._isFirstStart;
    }
    /** Elements */
    get elements() {
        return this._elements;
    }
    constructor(initialProps, canInit = true) {
        super(initialProps, false);
        this._isFirstStart = true;
        this._elements = [];
        this._isIntersectionObserverSupported = isIntersectionObserverSupported();
        // initialize the class
        if (canInit) {
            this.init();
        }
    }
    _init() {
        super._init();
        this._setEvents();
    }
    _onPropsMutate() {
        super._onPropsMutate();
        this.resize();
    }
    /** Set class events */
    _setEvents() {
        const { viewportTarget, resizeDebounce } = this.props;
        this.resize();
        this.addViewportCallback(viewportTarget, () => this.resize(), {
            timeout: resizeDebounce,
        });
    }
    /** Resize the scene */
    resize() {
        this._removeViewEvents();
        if (this.props.isEnabled) {
            this._setViewEvents();
        }
    }
    /** Root bounding rect */
    get rootBounding() {
        const { app, props } = this;
        if (props.intersectionRoot) {
            const bounding = props.intersectionRoot.getBoundingClientRect();
            return {
                top: bounding.top,
                left: bounding.left,
                width: bounding.width,
                height: bounding.height,
            };
        }
        const { viewport } = app;
        return {
            top: 0,
            left: 0,
            width: viewport.width,
            height: viewport.height,
        };
    }
    /** Set view events */
    _setViewEvents() {
        if (!this._isIntersectionObserverSupported) {
            return;
        }
        const { rootBounding, isFirstStart, props } = this;
        const xMargin = isFirstStart
            ? 0
            : rootBounding.width * props.rootMargin * -1;
        const yMargin = isFirstStart
            ? 0
            : rootBounding.height * props.rootMargin * -1;
        this._intersectionObserverIn = new IntersectionObserver((data) => this._handleIntersectionIn(data), {
            root: props.intersectionRoot,
            threshold: 0,
            rootMargin: `0px ${xMargin}px ${yMargin}px 0px`,
        });
        this.elements.forEach((element) => { var _a; return (_a = this._intersectionObserverIn) === null || _a === void 0 ? void 0 : _a.observe(element); });
        if (props.states === 'inout') {
            this._intersectionObserverOut = new IntersectionObserver((data) => this._handleIntersectionOut(data), {
                root: props.intersectionRoot,
                threshold: 0,
                rootMargin: '0px 0px 0px 0px',
            });
            this.elements.forEach((element) => { var _a; return (_a = this._intersectionObserverOut) === null || _a === void 0 ? void 0 : _a.observe(element); });
        }
    }
    /** Remove View events */
    _removeViewEvents() {
        var _a, _b;
        (_a = this._intersectionObserverIn) === null || _a === void 0 ? void 0 : _a.disconnect();
        this._intersectionObserverIn = undefined;
        (_b = this._intersectionObserverOut) === null || _b === void 0 ? void 0 : _b.disconnect();
        this._intersectionObserverOut = undefined;
    }
    /** Event on IntersectionObserver In */
    _handleIntersectionIn(data) {
        data.forEach((entry) => {
            if (!entry.isIntersecting) {
                return;
            }
            const element = entry.target;
            const delay = this._getElementDelay(element);
            this._handleInOut(element, true, delay);
            if (this.props.states === 'in') {
                this.removeElement(element);
            }
        });
        // change states
        if (this.isFirstStart) {
            this._isFirstStart = false;
            this.resize();
        }
    }
    /** Get element delay */
    _getElementDelay(element) {
        const { isFirstStart, props } = this;
        const rootBounding = isFirstStart ? this.rootBounding : false;
        if (!isFirstStart || !rootBounding || !props.hasDelay) {
            return 0;
        }
        const bounding = element.getBoundingClientRect();
        const progress = clamp(props.direction === 'horizontal'
            ? (bounding.left - rootBounding.left) / rootBounding.width
            : (bounding.top - rootBounding.top) / rootBounding.height, [0, 1]);
        return progress * props.maxDelay;
    }
    /** Event on IntersectionObserver Out */
    _handleIntersectionOut(data) {
        data.forEach((entry) => {
            if (entry.isIntersecting) {
                return;
            }
            const element = entry.target;
            this._handleInOut(element, false);
        });
    }
    /**
     * Event that is triggered when an element appears or disappears
     */
    _handleInOut(elementProp, isInViewport, delay = 0) {
        const { classToToggle } = this.props;
        const element = elementProp;
        if ((element.isScrollViewIn && isInViewport) ||
            (!element.isScrollViewIn && !isInViewport)) {
            return;
        }
        // update props
        element.isScrollViewIn = isInViewport;
        // toggle classes
        if (classToToggle) {
            normalizedTimeoutCallback(() => {
                element.classList.toggle(classToToggle, isInViewport);
            }, delay);
        }
        // process callbacks
        if (isInViewport) {
            normalizedTimeoutCallback(() => {
                this.callbacks.tbt('in', { element });
            }, delay);
        }
        else {
            normalizedTimeoutCallback(() => {
                this.callbacks.tbt('out', { element });
            }, delay);
        }
    }
    /** Add an element */
    addElement(elementProp) {
        var _a, _b;
        const element = elementProp;
        element.isScrollViewIn = undefined;
        if (this._isIntersectionObserverSupported) {
            this._elements.push(element);
            (_a = this._intersectionObserverIn) === null || _a === void 0 ? void 0 : _a.observe(element);
            (_b = this._intersectionObserverOut) === null || _b === void 0 ? void 0 : _b.observe(element);
        }
        else {
            this._handleInOut(element, true);
        }
        return {
            remove: () => this.removeElement(element),
        };
    }
    /** Remove an element */
    removeElement(elementProp) {
        var _a, _b;
        const element = elementProp;
        (_a = this._intersectionObserverIn) === null || _a === void 0 ? void 0 : _a.unobserve(element);
        (_b = this._intersectionObserverOut) === null || _b === void 0 ? void 0 : _b.unobserve(element);
        element.isScrollViewIn = undefined;
        this._elements = this._elements.filter((el) => el !== element);
    }
    /** Remove all elements */
    removeElements() {
        this._elements.forEach((element) => this.removeElement(element));
        this._elements = [];
    }
    /** Destroy the module */
    _destroy() {
        super._destroy();
        this._removeViewEvents();
    }
}
//# sourceMappingURL=index.js.map